import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

export default function PrivacyPolicyPage() {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen bg-white font-['Outfit',sans-serif] text-gray-900">
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');

        .text-gradient {
          background: linear-gradient(135deg, #FF6B35 0%, #FFA500 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>

            {/* Navigation */}
            <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-white'}`}>
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="flex justify-between items-center h-20">
                        <Link to="/" className="flex items-center gap-2">
                            <img src="/logo-qorvix.png" alt="Qorvix Logo" className="h-10 w-auto" />
                        </Link>

                        <div className="hidden lg:flex items-center space-x-8">
                            <Link to="/" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">Home</Link>
                            <Link to="/about" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">About</Link>
                            <Link to="/how-it-works" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">How it works</Link>
                            <Link to="/portfolio" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">Portfolio</Link>
                            <Link to="/contact" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">Contact us</Link>
                        </div>

                        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden text-gray-900">
                            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
                        </button>
                    </div>
                </div>
            </nav>

            {mobileMenuOpen && (
                <div className="fixed inset-0 z-50 lg:hidden">
                    <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)}></div>
                    <div className="fixed top-8 left-1/2 -translate-x-1/2 w-[90%] max-w-sm bg-white rounded-3xl shadow-2xl p-8 z-50">
                        <button onClick={() => setMobileMenuOpen(false)} className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors">
                            <X size={24} />
                        </button>
                        <nav className="mt-4 space-y-6">
                            <Link to="/" className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>Home</Link>
                            <Link to="/about" className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>About</Link>
                            <Link to="/how-it-works" className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>How it works</Link>
                            <Link to="/portfolio" className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>Portfolio</Link>
                            <Link to="/contact" className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>Contact us</Link>
                        </nav>
                    </div>
                </div>
            )}

            {/* Hero */}
            <section className="pt-32 pb-16 px-4 bg-gradient-to-br from-orange-50 to-white">
                <div className="max-w-4xl mx-auto text-center">
                    <h1 className="text-5xl md:text-6xl font-bold mb-6">
                        <span className="text-gradient">Privacy Policy</span>
                    </h1>
                    <p className="text-lg text-gray-700">Last updated: April 22, 2026</p>
                </div>
            </section>

            {/* Content */}
            <section className="py-16 px-4">
                <div className="max-w-4xl mx-auto prose prose-lg">
                    <div className="space-y-10 text-gray-700 leading-relaxed">

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
                            <p>
                                Welcome to Qorvix Technologies ("Qorvix", "we", "our", or "us"). We respect your privacy and are committed to protecting the personal information you share with us. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, use our services, or interact with us in any way.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
                            <p className="mb-3">We may collect the following types of information:</p>
                            <ul className="list-disc pl-6 space-y-2">
                                <li><strong>Personal Information:</strong> Name, email address, phone number, company name, company website, company address, and job-related details you voluntarily provide through our forms.</li>
                                <li><strong>Business Information:</strong> Details about your business type, growth stage, operational challenges, budget, and timeline.</li>
                                <li><strong>Technical Data:</strong> IP address, browser type, device information, pages visited, and time spent on our site.</li>
                                <li><strong>Communication Data:</strong> Records of your interactions with us through email, WhatsApp, forms, or chat.</li>
                            </ul>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
                            <p className="mb-3">We use your information to:</p>
                            <ul className="list-disc pl-6 space-y-2">
                                <li>Provide, operate, and improve our services</li>
                                <li>Respond to inquiries and process demo or consultation requests</li>
                                <li>Send service-related communications and updates</li>
                                <li>Personalize your experience and tailor our recommendations</li>
                                <li>Comply with legal obligations and enforce our terms</li>
                                <li>Analyze usage trends to improve our website and offerings</li>
                            </ul>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Sharing Your Information</h2>
                            <p>
                                We do not sell or rent your personal information. We may share your data with trusted third-party service providers (such as email, CRM, and analytics tools) who assist us in operating our business, subject to strict confidentiality agreements. We may also disclose information if required by law or to protect our legal rights.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Security</h2>
                            <p>
                                We implement reasonable administrative, technical, and physical safeguards to protect your personal information. However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Cookies and Tracking</h2>
                            <p>
                                We use cookies and similar tracking technologies to enhance your browsing experience, analyze site traffic, and understand where our visitors come from. You can control cookies through your browser settings.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Your Rights</h2>
                            <p className="mb-3">Depending on your location, you may have the right to:</p>
                            <ul className="list-disc pl-6 space-y-2">
                                <li>Access the personal information we hold about you</li>
                                <li>Request correction or deletion of your data</li>
                                <li>Object to or restrict the processing of your information</li>
                                <li>Withdraw consent where processing is based on consent</li>
                                <li>Lodge a complaint with a data protection authority</li>
                            </ul>
                            <p className="mt-3">To exercise these rights, contact us at <a href="mailto:support@qorvix.ai" className="text-orange-500 hover:underline">support@qorvix.ai</a>.</p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Third-Party Links</h2>
                            <p>
                                Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of those sites. We encourage you to review their privacy policies before providing any personal information.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Children's Privacy</h2>
                            <p>
                                Our services are not directed to individuals under the age of 16. We do not knowingly collect personal information from children. If we learn that we have collected such information, we will take steps to delete it.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Changes to This Policy</h2>
                            <p>
                                We may update this Privacy Policy from time to time. The updated version will be indicated by a revised "Last updated" date. We encourage you to review this page periodically.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Contact Us</h2>
                            <p>If you have any questions about this Privacy Policy, please contact us:</p>
                            <div className="mt-4 p-6 bg-gray-50 rounded-lg">
                                <p><strong>Qorvix Technologies</strong></p>
                                <p>London, England, UK</p>
                                <p>Email: <a href="mailto:support@qorvix.ai" className="text-orange-500 hover:underline">support@qorvix.ai</a></p>
                                <p>Phone: +44 7757 233260</p>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-white py-12 px-4 border-t border-gray-200">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-4 gap-8 mb-8">
                        <div>
                            <h3 className="text-xl font-bold mb-4">
                                <span className="text-gradient">Qorvix</span> — Automate What Matters
                            </h3>
                            <p className="text-gray-600 text-sm leading-relaxed mb-6">
                                Qorvix helps businesses automate workflows, connect tools, and scale faster using AI-powered systems.
                            </p>
                        </div>

                        <div>
                            <h4 className="font-bold mb-4">Contact</h4>
                            <div className="space-y-2 text-sm text-gray-600">
                                <p>London, England, UK</p>
                                <p>+44 7757 233260</p>
                                <p>Support@qorvix.ai</p>
                            </div>
                        </div>

                        <div>
                            <h4 className="font-bold mb-4">Company</h4>
                            <div className="space-y-2 text-sm">
                                <Link to="/" className="block text-gray-600 hover:text-gray-900 transition-colors">Home</Link>
                                <Link to="/about" className="block text-gray-600 hover:text-gray-900 transition-colors">About</Link>
                                <Link to="/portfolio" className="block text-gray-600 hover:text-gray-900 transition-colors">Portfolio</Link>
                                <Link to="/contact" className="block text-gray-600 hover:text-gray-900 transition-colors">Contact</Link>
                            </div>
                        </div>

                        <div>
                            <h4 className="font-bold mb-4">Legal</h4>
                            <div className="space-y-2 text-sm">
                                <Link to="/privacy-policy" className="block text-gray-600 hover:text-gray-900 transition-colors">Privacy Policy</Link>
                                <Link to="/terms-of-service" className="block text-gray-600 hover:text-gray-900 transition-colors">Terms of Service</Link>
                            </div>
                        </div>
                    </div>

                    <div className="pt-8 flex items-center justify-between border-t border-gray-200">
                        <div className="text-2xl font-bold text-gradient">Qorvix</div>
                        <div className="text-sm text-gray-600">
                            © Qorvix Technologies. All Rights Reserved.
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
